/**
 * lib/cohort.ts
 * ------------------------------------------------------------------
 * The June intake (AS/6L + A2 "June") runs on its own calendar: it is in
 * ITS Term 1 while the January intake is in Term 2. Its marks are entered
 * under the school-global term (currently Term 2, single "CA"), but its
 * marksheets, reports and analysis must present as their own Term 1 —
 * CA-only, comment-only, no exam.
 *
 * EVERY June-specific behaviour is gated on `isJuneCohort()` so the
 * January path is never altered: when a class is not June, callers get
 * exactly the values they had before.
 * ------------------------------------------------------------------ */
import type { SchoolClass } from '@/types';

/** True for the out-of-cycle June-intake classes. Prefers the `calendar`
 *  tag; falls back to the class name so it works even before tagging. */
export function isJuneCohort(cls?: Partial<Pick<SchoolClass, 'calendar' | 'name'>> | null): boolean {
  if (!cls) return false;
  if (cls.calendar === 'june') return true;
  const n = (cls.name || '').trim().toLowerCase();
  return n === 'as june' || n === 'a2 june';
}

/** Term label shown for a June-cohort class (their own Term 1). */
export const JUNE_TERM_LABEL = 'Term 1';

/**
 * Whether an exam column applies, given the global term AND the class.
 * June classes never carry an exam this term (they are CA-only Term 1),
 * regardless of what the global term would normally use.
 */
export function classUsesExam(globalUsesExam: boolean, cls?: Pick<SchoolClass, 'calendar' | 'name'> | null): boolean {
  return isJuneCohort(cls) ? false : globalUsesExam;
}

/** The term label to DISPLAY for a class — June shows their Term 1, everyone
 *  else shows the school-global term's label. */
export function classTermLabel(globalTermLabel: string, cls?: Pick<SchoolClass, 'calendar' | 'name'> | null): string {
  return isJuneCohort(cls) ? JUNE_TERM_LABEL : globalTermLabel;
}

/* ==================================================================
 * BILLING
 * ------------------------------------------------------------------
 * The same split, asked for a different reason. Academics above needs to
 * know that a June class presents as its own Term 1; FEES needs to know
 * that the June intake is in a different term from the January one and
 * falls due on different dates. Both questions are answered by the same
 * `calendar` tag, so there is one definition of which calendar a class is
 * on and not two that can disagree.
 *
 * A cohort says WHEN a pupil is billed, never HOW MUCH. Both intakes pay
 * the A-level fee; `formBandForForm` still returns 'AL' for either, and
 * nothing here may be used to charge two pupils in the same year
 * differently.
 * ================================================================== */

export type BillingCohort = 'main' | 'alJan' | 'alJune' | 'preSchool';

/**
 * A pre-school class: form "DC" (Day Care), or named Day Care / Pre-School /
 * Nursery. Pre-school is billed as its own group (Oct 2026): the bursar
 * invoices it separately, and it has its own two fee bands (PRE_LOWER /
 * PRE_UPPER in financeCore). Lives here, not in financeCore, because
 * financeCore imports this file.
 */
export function isPreSchoolClass(cls?: { form?: unknown; name?: string } | null): boolean {
  const form = String(cls?.form ?? '').trim().toUpperCase();
  return form === 'DC' || /\b(day ?care|pre-?school|nursery)\b/i.test(String(cls?.name ?? ''));
}

/** As much of a class as any of this needs, and no more. */
export type BillingClassRef =
  Partial<Pick<SchoolClass, 'calendar' | 'name' | 'form'>> & { id?: string };

export const COHORT_LABELS: Record<BillingCohort, string> = {
  main: 'Main school (Forms 1–5)',
  alJan: 'A-level — January intake',
  alJune: 'A-level — June intake',
  preSchool: 'Pre-school (Lower & Upper)',
};

/** Is this an A-level class at all, whichever intake? */
export function isALevelClass(cls?: BillingClassRef | null): boolean {
  if (!cls) return false;
  // Word-bounded: "Form 2 A" is not A2, and billing a Form 2 class on the
  // A-level calendar would be a bad afternoon.
  return /\bAS\b|\bA2\b|A-?LEVEL/.test(`${cls.form ?? ''} ${cls.name ?? ''}`.toUpperCase());
}

/**
 * Which group a class is billed with.
 *
 * An A-level class that is not tagged `june` is the January intake, which is
 * the ordinary calendar — the same default `isJuneCohort` already applies.
 */
export function billingCohortOf(cls?: BillingClassRef | null): BillingCohort {
  if (isPreSchoolClass(cls)) return 'preSchool';
  if (!isALevelClass(cls)) return 'main';
  return isJuneCohort(cls) ? 'alJune' : 'alJan';
}

/** Cohort for every class, by id — built once and handed to the filters. */
export function cohortByClassId(
  classes: readonly BillingClassRef[],
): Record<string, BillingCohort> {
  const out: Record<string, BillingCohort> = {};
  for (const c of classes) if (c.id) out[c.id] = billingCohortOf(c);
  return out;
}

/**
 * Keep only the pupils in one cohort.
 *
 * A pupil with no class — not yet placed — belongs to the main school, which
 * is where an unplaced pupil is billed today. Being explicit about it means
 * an A-level run cannot quietly pick them up.
 */
export function studentsInCohort<T extends { currentClass?: string }>(
  students: readonly T[],
  byClass: Record<string, BillingCohort>,
  cohort: BillingCohort,
): T[] {
  return students.filter((s) => (byClass[s.currentClass ?? ''] ?? 'main') === cohort);
}

/** How many pupils sit in each cohort — for the picker, so nobody bills 0. */
export function cohortCounts<T extends { currentClass?: string }>(
  students: readonly T[],
  byClass: Record<string, BillingCohort>,
): Record<BillingCohort, number> {
  const out: Record<BillingCohort, number> = { main: 0, alJan: 0, alJune: 0, preSchool: 0 };
  for (const s of students) out[byClass[s.currentClass ?? ''] ?? 'main'] += 1;
  return out;
}

/**
 * How a cohort's term is named ON A DOCUMENT A PARENT READS.
 *
 * The June intake's Term 2 runs while the school is in Term 3, so an invoice
 * headed plainly "Term 2" invites the reply "but we are in Term 3". Saying
 * "Term 2 (June Intake)" answers that before it is asked.
 *
 * The suffix is presentation only. `invoice.term` stays a TermKey, because
 * every piece of logic that groups, compares or guards on a term needs one —
 * the "already billed this term" check most of all.
 */
export const COHORT_SUFFIX: Record<BillingCohort, string> = {
  main: '',
  alJan: '',
  alJune: ' (June Intake)',
  preSchool: '',
};

export function termLabelForCohort(baseLabel: string, cohort?: BillingCohort | null): string {
  return `${baseLabel}${COHORT_SUFFIX[cohort ?? 'main'] ?? ''}`;
}
